import type { BadgeProps } from '@/ui';
import { formatAmount } from './player-balance';

type Tone = NonNullable<BadgeProps['tone']>;

/** Front-office payment provider from UsersAPI `ProviderFrontDTO`. */
export interface PaymentProvider {
  Id: number;
  Name: string;
  Logo?: string | null;
  Fee?: number | null;
  Duration?: string | null;
  MinAmount?: number | null;
  MaxAmount?: number | null;
  Description?: string | null;
  LaunchUrl?: string | null;
  ApiUrl?: string | null;
  AccountProviderId?: number | null;
  MachineProviderId?: number | null;
  IsRedeposit?: boolean;
  DisplayIndex?: number | null;
  DefaultCurrencyCode?: string | null;
  DefaultProviderCurrencyCode?: string | null;
  DefaultCurrency?: number | null;
  DefaultProviderCurrencyValue?: number | null;
  DefaultMachineCurrencyValue?: number | null;
  ConvertCurrency?: boolean;
}

/** Deposit / withdrawal history row (`MachineDepositsView`). */
export interface PaymentRequestRow {
  Id?: number | null;
  EncId?: string | null;
  Type?: string | null;
  Amount?: number | null;
  ApprovedAmount?: number | null;
  Status?: number | null;
  Data?: string | null;
  Date?: string | null;
  BonusName?: string | null;
  Currency?: string | null;
}

export interface PaymentBank {
  Id: number | string;
  Name: string;
  Code?: string | null;
}

export interface PaymentWalletType {
  Id: number | string;
  Name: string;
}

/** UsersAPI `PlayerAvailableBonusesListItem` for deposit-form bonus select. */
export interface DepositBonusOption {
  Id: string;
  Name?: string | null;
  Type?: string | null;
  MinDepositAmount?: number | null;
  MaxDepositAmount?: number | null;
  PageTitle?: string | null;
  PageUrl?: string | null;
}

/** UsersAPI `PlayerAvailableBonusesResponse` from `depositbonuses`. */
export interface DepositBonusesResponse {
  Success?: boolean;
  Message?: string | null;
  DepositBonuses?: DepositBonusOption[] | null;
}

export interface EffectiveDepositLimits {
  minAmount: number;
  maxAmount: number;
}

/**
 * Intersect provider deposit limits with the selected bonus (legacy `BonusConfirm`).
 * Missing / non-finite bonus bounds leave the provider limit unchanged.
 */
export function effectiveDepositLimits(
  provider: Pick<PaymentProvider, 'MinAmount' | 'MaxAmount'>,
  bonus?: Pick<DepositBonusOption, 'MinDepositAmount' | 'MaxDepositAmount'> | null,
): EffectiveDepositLimits {
  const providerMin = Number(provider.MinAmount);
  const providerMax = Number(provider.MaxAmount);
  const minAmount = Number.isFinite(providerMin) ? providerMin : 0;
  const maxAmount = Number.isFinite(providerMax)
    ? providerMax
    : Number.POSITIVE_INFINITY;

  if (!bonus) return { minAmount, maxAmount };

  const bonusMin = Number(bonus.MinDepositAmount);
  const bonusMax = Number(bonus.MaxDepositAmount);
  return {
    minAmount: Number.isFinite(bonusMin) ? Math.max(minAmount, bonusMin) : minAmount,
    maxAmount: Number.isFinite(bonusMax) ? Math.min(maxAmount, bonusMax) : maxAmount,
  };
}

/** Normalize UsersAPI deposit-bonuses payload into options + eligibility message. */
export function parseDepositBonusesResponse(raw: unknown): {
  bonuses: DepositBonusOption[];
  message: string | null;
  success: boolean;
} {
  if (Array.isArray(raw)) {
    return { bonuses: raw as DepositBonusOption[], message: null, success: true };
  }
  if (!raw || typeof raw !== 'object') {
    return { bonuses: [], message: null, success: false };
  }
  const response = raw as DepositBonusesResponse;
  if (response.Success === false) {
    const message = typeof response.Message === 'string' ? response.Message.trim() : '';
    return { bonuses: [], message: message || null, success: false };
  }
  const list = Array.isArray(response.DepositBonuses) ? response.DepositBonuses : [];
  return { bonuses: list, message: null, success: true };
}

export interface WalletPaymentResponse {
  Success?: boolean;
  Error?: string | null;
  Result?: string | null;
  Data?: unknown;
  DepositInfo?: unknown;
  CryptoData?: unknown;
  P2pCisData?: unknown;
  TransactionId?: string | null;
}

export const PAYMENT_HISTORY_PAGE_SIZE = 10;
export const PAYMENT_QUICK_AMOUNTS = [10, 20, 50, 100] as const;

/**
 * Deposits and withdrawals shared one status set and one colour map in the legacy
 * code (`Mappings.DepositStatuses` / `RequestColors`), so they share them here too.
 */
export const PaymentRequestStatus = {
  New: 0,
  Processing: 1,
  Declined: 2,
  Completed: 3,
} as const;

export type PaymentRequestStatus = (typeof PaymentRequestStatus)[keyof typeof PaymentRequestStatus];

export const PAYMENT_REQUEST_STATUS_LABEL: Record<PaymentRequestStatus, string> = {
  [PaymentRequestStatus.New]: 'New',
  [PaymentRequestStatus.Processing]: 'Processing',
  [PaymentRequestStatus.Declined]: 'Declined',
  [PaymentRequestStatus.Completed]: 'Completed',
};

export const PAYMENT_REQUEST_STATUS_TONE: Record<PaymentRequestStatus, Tone> = {
  [PaymentRequestStatus.New]: 'info',
  [PaymentRequestStatus.Processing]: 'warning',
  [PaymentRequestStatus.Declined]: 'danger',
  [PaymentRequestStatus.Completed]: 'success',
};

export const PaymentAccountStatus = {
  Pending: 0,
  Verified: 1,
  Declined: 2,
} as const;

export type PaymentAccountStatus = (typeof PaymentAccountStatus)[keyof typeof PaymentAccountStatus];

export const PAYMENT_ACCOUNT_STATUS_LABEL: Record<PaymentAccountStatus, string> = {
  [PaymentAccountStatus.Pending]: 'Pending',
  [PaymentAccountStatus.Verified]: 'Verified',
  [PaymentAccountStatus.Declined]: 'Declined',
};

export const PAYMENT_ACCOUNT_STATUS_TONE: Record<PaymentAccountStatus, Tone> = {
  [PaymentAccountStatus.Pending]: 'warning',
  [PaymentAccountStatus.Verified]: 'success',
  [PaymentAccountStatus.Declined]: 'danger',
};

/** The API sends `null` for a player who has never uploaded a document. */
export const IdentityStatus = {
  Unverified: 0,
  Pending: 1,
  Verified: 2,
} as const;

export type IdentityStatus = (typeof IdentityStatus)[keyof typeof IdentityStatus];

export function identityStatusOf(value: number | null | undefined): IdentityStatus {
  return value === IdentityStatus.Pending || value === IdentityStatus.Verified
    ? value
    : IdentityStatus.Unverified;
}

export const IDENTITY_STATUS_LABEL: Record<IdentityStatus, string> = {
  [IdentityStatus.Unverified]: 'Unverified',
  [IdentityStatus.Pending]: 'Pending',
  [IdentityStatus.Verified]: 'Verified',
};

export const IDENTITY_STATUS_TONE: Record<IdentityStatus, Tone> = {
  [IdentityStatus.Unverified]: 'danger',
  [IdentityStatus.Pending]: 'warning',
  [IdentityStatus.Verified]: 'success',
};

/** Ported from `Enums.ActivityTypes`, which drives the account activity ledger. */
export const ACTIVITY_TYPE_LABEL: Record<number, string> = {
  0: 'Credits',
  1: 'Register',
  2: 'Spin',
  3: 'Spin win',
  4: 'Jackpot',
  5: 'Ticket',
  6: 'Ticket win',
  7: 'Ticket refund',
  8: 'Ticket lost',
  9: 'Deposit',
  10: 'Deposit accepted',
  11: 'Deposit declined',
  12: 'Withdrawal request',
  13: 'Withdrawal accepted',
  14: 'Withdrawal declined',
  15: 'Bonus',
  16: 'System payout',
  17: 'Bonus cancelled',
  18: 'External game lost',
  19: 'External game won',
  20: 'Ticket sold',
  21: 'Deposit bonus',
  22: 'Bonus completed',
  23: 'Ticket rollback',
  24: 'Backup reward',
  25: 'Freebet reward',
  26: 'Manual bonus',
  27: 'Ticket partial cashout',
  28: 'Ticket auto cashout',
  31: 'Manual cancel',
};

export const TransactionType = {
  ManualCancel: 31,
} as const;

export function paymentStatusOf(value: number | null | undefined): PaymentRequestStatus {
  if (
    value === PaymentRequestStatus.New ||
    value === PaymentRequestStatus.Processing ||
    value === PaymentRequestStatus.Declined ||
    value === PaymentRequestStatus.Completed
  ) {
    return value;
  }
  return PaymentRequestStatus.New;
}

export function formatPaymentAmount(
  amount: number | null | undefined,
  currency = '',
  decimals = 2,
): string {
  const formatted = formatAmount(amount ?? 0, decimals);
  const code = currency.trim();
  return code ? `${code}${formatted}` : formatted;
}

export function formatProviderLimits(
  provider: Pick<PaymentProvider, 'MinAmount' | 'MaxAmount'>,
  currency = '',
): string {
  const min = formatPaymentAmount(provider.MinAmount ?? 0, currency);
  const max = formatPaymentAmount(provider.MaxAmount ?? 0, currency);
  return `${min} - ${max}`;
}

export function formatPaymentFee(fee: number | null | undefined): string {
  if (fee == null || !Number.isFinite(fee) || fee === 0) return 'Free';
  return `${fee}%`;
}

export function formatPaymentDuration(duration: string | null | undefined): string {
  const value = (duration ?? '').trim();
  return value || 'Instant';
}

export function filterProvidersBySearch(
  providers: PaymentProvider[],
  search: string,
): PaymentProvider[] {
  const term = search.trim().toLowerCase();
  if (!term) return providers;
  return providers.filter((provider) => provider.Name.toLowerCase().includes(term));
}

/**
 * Same ordering as UsersAPI `SportsBookCommonService.Providers`:
 * redeposit first → providers with DisplayIndex → DisplayIndex ascending.
 */
export function sortProvidersByPosition(providers: PaymentProvider[]): PaymentProvider[] {
  return [...providers].sort((a, b) => {
    const redeposit = Number(Boolean(b.IsRedeposit)) - Number(Boolean(a.IsRedeposit));
    if (redeposit !== 0) return redeposit;
    const aHas = a.DisplayIndex != null ? 1 : 0;
    const bHas = b.DisplayIndex != null ? 1 : 0;
    if (bHas !== aHas) return bHas - aHas;
    return (a.DisplayIndex ?? 0) - (b.DisplayIndex ?? 0);
  });
}

export function findProviderById(
  providers: PaymentProvider[],
  providerId: string | number,
): PaymentProvider | undefined {
  const id = typeof providerId === 'string' ? Number(providerId) : providerId;
  if (!Number.isFinite(id)) return undefined;
  return providers.find((provider) => provider.Id === id);
}

export function canCancelPayment(status: number | null | undefined): boolean {
  return status === PaymentRequestStatus.Processing;
}

export function extractPaymentRedirectUrl(response: WalletPaymentResponse): string | null {
  if (typeof response.Result === 'string' && /^https?:\/\//i.test(response.Result)) {
    return response.Result;
  }
  const data = response.Data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ['RedirectLink', 'RedirectUrl', 'Url', 'PaymentUrl']) {
      const value = record[key];
      if (typeof value === 'string' && /^https?:\/\//i.test(value)) return value;
    }
  }
  return null;
}

/** Flatten common inline payment instruction payloads for display. */
export function extractPaymentInstructions(
  response: WalletPaymentResponse,
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  const sources = [response.Data, response.DepositInfo, response.CryptoData, response.P2pCisData];
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
      if (value == null || value === '') continue;
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        rows.push({ label: key, value: String(value) });
      }
    }
  }
  if (rows.length === 0 && typeof response.Result === 'string' && response.Result.trim()) {
    rows.push({ label: 'Info', value: response.Result });
  }
  return rows;
}
